import AbstractUnit from './AbstractUnit';
import { COLOR_SCHEMES } from '../../../../constants/colors';
import {
    REF_SIZE,
    REF_SIZE_PT, SIZE_OPTIONS, UNIT
} from '../../../../constants/size';
import HeaderUnit from './HeaderUnit';
import GradientDissolveRect from '../../objects/GradientDissolveRect';
import GradientArc from '../../objects/GradientArc';
import PlaneDecorRectangle from '../../objects/PlaneDecorRectangle';
import { arcFlowersConfig } from '../../objects/configs/PlaneDecorConfig';
import { linear, clamp01 } from '../../../../utils/progress';

const THREE = require('three');

class SectionHeaderUnit extends AbstractUnit {
    _setup(data, stgs, webgl) {
        super._setup(data, stgs, webgl);

        const {
            sectionId, previousSectionId, scheme, previousScheme, theme, previousTheme
        } = this.data;

        this.name = `${data.name}-unit-header`;

        this.hasResize = true;
        this.hasProgressAnimation = true;
        this.isAfterChangeSchemePoint = false;
        this.canChangeScheme = true;
        this.canReciveScheme = true;

        this._scheme = scheme;
        this._theme = theme;
        this._sectionId = sectionId;
        this._previousScheme = previousScheme;
        this._previousTheme = previousTheme;
        this._previousSectionId = previousSectionId;
        this._schemeConfig = COLOR_SCHEMES[scheme];
        this._prevSchemeCongif = COLOR_SCHEMES[previousScheme];

        // Default
        this._orientation = SIZE_OPTIONS.LANDSCAPE;
    }

    _constructView(data, webgl, stgs, factory, orientation) {
        super._constructView(data, webgl, stgs, factory, orientation);

        const { bg } = this._schemeConfig;
        const bg2 = this._prevSchemeCongif.bg;

        // Exclude unnecessary data parameters
        const {
            theme, index, title, titlePlane,
            subtitle, subtitlePlane, subtitleWidthEM, unit
        } = data;
        const textUnit = new HeaderUnit({
            theme,
            index,
            title,
            titlePlane,
            subtitle,
            subtitlePlane,
            subtitleWidthEM,
            unit
        }, webgl, stgs);

        const dissolveRect = new GradientDissolveRect(bg);

        const arcGroup = new THREE.Group();
        const arcMesh = new GradientArc(bg2);
        const arcDecorMesh = new PlaneDecorRectangle(arcFlowersConfig);

        dissolveRect.scale.set(1.6, 2, 1);
        arcDecorMesh.position.z = 0.03 * UNIT.zSize;

        arcGroup.add(arcMesh);
        arcGroup.add(arcDecorMesh);

        this.add(textUnit);
        this.add(dissolveRect);
        this.add(arcGroup);

        this.textUnit = textUnit;
        this.mainView = textUnit;

        this.renderOrder = 1;
        dissolveRect.renderOrder = 2;
        textUnit.renderOrder = 5;
        arcGroup.renderOrder = 11;
        arcMesh.renderOrder = 11;
        arcDecorMesh.renderOrder = 12;

        this.dissolveRect = dissolveRect;
        this.arcGroup = arcGroup;
        this.arcMesh = arcMesh;
        this.arcDecorMesh = arcDecorMesh;
    }

    resize(winWidth, winHeight, orientation, force, pixelRatio) {
        const sizesScale = UNIT.sizesScaleRatio;
        const prevOrientation = this._orientation;

        super.resize(winWidth, winHeight, orientation, force, pixelRatio);

        if (this.textUnit.hasResize) this.textUnit.resize(winWidth, winHeight, orientation, force);
        if (this.objectsGroup2 && this.objectsGroup) this.objectsGroup2.position.z = this.objectsGroup.position.z;

        this.dissolveRect.viewportWidth = winWidth * pixelRatio;
        this.dissolveRect.viewportHeight = winHeight * pixelRatio;
        this.arcMesh.viewportWidth = winWidth * pixelRatio;
        this.arcMesh.viewportHeight = winHeight * pixelRatio;

        // this.dissolveRect.opacity = 0;

        if (orientation === prevOrientation && !force) return;

        let arcScale;
        let arcY = 0;
        let textDY = 0;

        if (orientation === SIZE_OPTIONS.LANDSCAPE) {
            arcScale = 0.57;
            arcY = 0.016 * REF_SIZE.h * sizesScale;
            textDY = 0.015 * REF_SIZE.h * sizesScale;

            if (this.data.isMajor) textDY = -0.12 * REF_SIZE.h * sizesScale;
        } else {
            arcScale = 1.4;
            arcY = 0.1 * REF_SIZE_PT.h * sizesScale;
            textDY = 0.05 * REF_SIZE_PT.h * sizesScale;
            if (this.data.isMajor) textDY = -0.1 * REF_SIZE.h * sizesScale;
        }

        this.textUnit.position.y = arcY + textDY;

        this.arcGroup.scale.set(arcScale, arcScale, 1);
        this.arcGroup.position.set(
            0,
            arcY,
            -this.mainViewZ + 0.7 * UNIT.zSize
        );

        this.dissolveRect.position.set(
            0,
            arcY,
            -this.mainViewZ - 0.1 * UNIT.zSize
        );
    }

    checkSchemeChanged() {
        const { camera } = this.webgl;

        const arcMtx = this.arcMesh.matrixWorld;
        const arcWorldPosition = new THREE.Vector3();

        arcWorldPosition.setFromMatrixPosition(arcMtx);

        const z = arcWorldPosition.z - camera.position.z;
        const newValue = z > -0.3 * UNIT.zSize;
        const changed = this.isAfterChangeSchemePoint !== newValue;
        let newScheme = this._previousScheme;
        let newTheme = this._previousTheme;
        let otherTheme = this._theme;
        let otherScheme = this._scheme;

        if (newValue) {
            newScheme = this._scheme;
            newTheme = this._theme;
            otherTheme = this._previousTheme;
            otherScheme = this._previousScheme;
        }

        this.isAfterChangeSchemePoint = newValue;

        return {
            changed,
            newScheme,
            newTheme,
            prevTheme: changed ? otherTheme : newTheme,
            prevScheme: changed ? otherScheme : newScheme
        };
    }

    checkNavigationChanged() {
        const { camera } = this.webgl;

        const arcMtx = this.arcMesh.matrixWorld;
        const arcWorldPosition = new THREE.Vector3();

        arcWorldPosition.setFromMatrixPosition(arcMtx);

        const z = arcWorldPosition.z - camera.position.z;
        const newValue = z > -0.3 * UNIT.zSize;
        const changed = this.isAfterChangeSchemePoint !== newValue;
        let newScheme = this._previousScheme;
        let newTheme = this._previousTheme;
        let otherTheme = this._theme;
        let otherScheme = this._scheme;

        if (newValue) {
            newScheme = this._scheme;
            newTheme = this._theme;
            otherTheme = this._previousTheme;
            otherScheme = this._previousScheme;
        }

        this.isAfterChangeSchemePoint = newValue;

        return {
            changed,
            newScheme,
            newTheme,
            prevTheme: changed ? otherTheme : newTheme,
            prevScheme: changed ? otherScheme : newScheme
        };
    }

    _updateAnimation(newProgress) {
        this.dissolveRect.opacity = 1 - clamp01(linear(newProgress, 0.001, 0.1));
        this.arcDecorMesh.opacity = clamp01(linear(newProgress, -0.42, -0.3));
    }

    get objectsZ() {
        return this.mainViewZ;
    }
}

export default SectionHeaderUnit;
